import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  Typography,
  Icon,
  Spacing,
} from '@taleemabad/dsm/components';
import { FC, useEffect, useState } from 'react';

export interface CoachingOnboardingDialogProps {
  open: boolean;
  onClose: () => void;
  onTryItNow: () => void;
  videoUrl: string;
  onPlay?: () => void;
}

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/**
 * Onboarding dialog for the "Class Insights" (Teaching Review) feature.
 * Displays an educational video and feature description to the user.
 *
 * @param props - Component properties
 * @param props.open - Whether the dialog is visible
 * @param props.onClose - Callback when the dialog is closed or dismissed
 * @param props.onTryItNow - Callback when the primary CTA is clicked
 * @param props.videoUrl - URL of the onboarding video (supports MP4/MOV)
 * @param props.onPlay - Optional callback when the video starts playing
 */
export const CoachingOnboardingDialog: FC<CoachingOnboardingDialogProps> = ({
  open,
  onClose,
  onTryItNow,
  videoUrl,
  onPlay,
}) => {
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setIsRendered(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsRendered(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      hasBackdropClick={true}
      emotionCss={css`
        padding: 0;
        gap: 0;
        & > div {
          padding: 0;
          gap: 0;
        }
      `}
    >
      <StyledDialogContent isRendered={isRendered}>
        <StyledCloseButton onClick={onClose} type="button">
          <Icon name="close" size="tiny" />
        </StyledCloseButton>

        <StyledVideoContainer>
          {open && (
            <video
              key={videoUrl}
              src={videoUrl}
              width="100%"
              height="100%"
              controls
              playsInline
              preload="metadata"
              style={{ objectFit: 'contain', display: 'block' }}
              onPlay={onPlay}
              onError={(e) => {
                const video = e.currentTarget as HTMLVideoElement;
                console.error('Video Load Error', {
                  code: video.error?.code,
                  message: video.error?.message,
                  url: videoUrl,
                });
              }}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </StyledVideoContainer>

        <StyledContentArea>
          <StyledHeaderRow>
            <Typography variant="textLabelSmallSecondary">
              New feature announcement
            </Typography>
            <StyledNewBadge>New</StyledNewBadge>
          </StyledHeaderRow>

          <Typography variant="textTitleLarge" textAlign="left" fullWidth>
            Try Class Insights
          </Typography>

          <Spacing marginTop="xs" />

          <Typography
            variant="textBodyLargeOnSurfaceVariant"
            textAlign="left"
            fullWidth
          >
            You can now record your class and get helpful insights on your
            teaching.
          </Typography>
        </StyledContentArea>

        <StyledFooter>
          <Button variant="filled" width="100%" onClick={onTryItNow}>
            Try it Now!
          </Button>
        </StyledFooter>
      </StyledDialogContent>
    </Dialog>
  );
};

const StyledDialogContent = styled.div<{ isRendered: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 90vw;
  max-width: 400px;
  max-height: 90vh; /* Wrap content unless it hits screen limit */
  background: var(--md-sys-color-surface);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.2);
  opacity: ${(props) => (props.isRendered ? 1 : 0)};
  transform: ${(props) =>
    props.isRendered ? 'translateY(0)' : 'translateY(10px)'};
  transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
`;

const StyledCloseButton = styled.button`
  position: absolute;
  top: var(--rk-spacing-lg);
  right: var(--rk-spacing-lg);
  background: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.4);
    transform: scale(1.1);
  }
`;

const StyledVideoContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9; /* Switched to 16:9 to fit better on screen with text */
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`;

const StyledContentArea = styled.div`
  padding: var(--rk-spacing-xl) var(--rk-spacing-xl) var(--rk-spacing-lg)
    var(--rk-spacing-xl);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  animation: ${slideUp} 0.6s ease-out both;
  animation-delay: 0.2s;
`;

const StyledHeaderRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--rk-spacing-sm);
`;

const StyledNewBadge = styled.span`
  background: var(--md-sys-color-error);
  color: var(--md-sys-color-on-error);
  padding: 2px 10px;
  border-radius: 100px;
  text-transform: uppercase;
  font-weight: 800;
  font-family: var(--md-sys-typescale-label-small-font);
  font-size: var(--md-sys-typescale-label-small-size);
  letter-spacing: 0.5px;
`;

const StyledFooter = styled.div`
  padding: 0 var(--rk-spacing-xl) var(--rk-spacing-xl) var(--rk-spacing-xl);
  flex-shrink: 0;
  background: var(--md-sys-color-surface);
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.6s ease-out both;
  animation-delay: 0.4s;

  md-filled-button {
    height: 52px;
    --md-filled-button-container-shape: 26px;
    --md-filled-button-label-text-size: 16px;
    --md-filled-button-label-text-weight: 700;
    --md-filled-button-container-color: #2e4a85;
    --md-filled-button-label-text-color: #ffffff;
  }
`;
